// src/authConfig.js
export const DEFAULT_AAD_AUTHORITY_HOST = "https://login.microsoftonline.com";

// A bare http(s) origin: scheme, host, optional port. No path, no whitespace.
const AUTHORITY_ORIGIN_RE = /^https?:\/\/[A-Za-z0-9.-]+(:\d+)?$/;

// ASCII whitespace only, matching POSIX [[:space:]] under LC_ALL=C, which is
// what the Dockerfile uses. Deliberately NOT String.prototype.trim(): trim also
// strips U+00A0 and other Unicode spaces, the shell does not, and that
// difference alone made the two halves disagree.
const ASCII_WS = /^[ \t\n\r\f\v]+|[ \t\n\r\f\v]+$/g;

// Must stay in lockstep with the AUTH_HOST block in frontend/Dockerfile: MSAL
// reads this value and the nginx CSP connect-src is generated from the same
// one, so if they resolve differently the browser blocks a token request the
// app believes is correctly aimed.
//
// What makes them agree is the VALIDATION, not the trimming. Anything that is
// not a bare origin after an ASCII-whitespace trim and trailing-slash strip
// falls back to AzureCloud, so inputs the two might clean differently — an
// embedded space, U+00A0, "///" — land on the same answer either way.
export function normalizeAuthorityHost(raw) {
  const value = raw || "";
  // A newline disqualifies the value outright, checked BEFORE any trimming.
  // The shell cannot clean a multiline value the way JS does — `sed` operates
  // line by line and command substitution strips trailing newlines — so
  // "\nhttps://host" and "https://host/\n/" each came out of the two sides
  // differently. The only way they agree on multiline input is to refuse it,
  // and an authority host never legitimately contains one.
  if (value.indexOf("\n") !== -1) return DEFAULT_AAD_AUTHORITY_HOST;
  const resolved = value.replace(ASCII_WS, "").replace(/\/+$/, "");
  return AUTHORITY_ORIGIN_RE.test(resolved) ? resolved : DEFAULT_AAD_AUTHORITY_HOST;
}

export const msalConfig = {
  auth: {
    clientId: process.env.REACT_APP_AAD_CLIENT_ID, // SPA app (frontend) Client ID
    // Host is configurable for sovereign clouds; defaults to AzureCloud so the
    // commercial build is unchanged. AzureUSGovernment is
    // https://login.microsoftonline.us. Whatever is set here must also appear in
    // the nginx CSP connect-src (see frontend/Dockerfile), or the browser blocks
    // the token request even though MSAL is pointed correctly. The Dockerfile
    // applies the same default to the CSP, so an empty build arg cannot point
    // MSAL at one cloud while connect-src names another.
    authority: `${normalizeAuthorityHost(process.env.REACT_APP_AAD_AUTHORITY_HOST)}/${process.env.REACT_APP_AAD_TENANT_ID}`,
    // Trailing slash matches the SPA redirect URIs registered on the Entra app.
    redirectUri: process.env.REACT_APP_REDIRECT_URI || `${window.location.origin}/`,
    postLogoutRedirectUri: process.env.REACT_APP_POSTLOGOUT_URI || window.location.origin,
  },
  cache: { cacheLocation: "sessionStorage" },
};

export const loginRequest = {
  // If your backend exposes an API scope, set it via env; otherwise fallback to a harmless MS scope.
  scopes: [process.env.REACT_APP_AAD_SCOPE || "User.Read"],
};
